import React from "react";

const DEFAULT_PREVIEW_FINANCIAL_YEAR = "2024-2025";

function normalizePreviewFinancialYear(financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR) {
  const value = String(financialYear || "").trim().replace(/^FY\s*/i, "");
  return value || DEFAULT_PREVIEW_FINANCIAL_YEAR;
}

function getPreviewFinancialYearLabel(financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR) {
  return `FY ${normalizePreviewFinancialYear(financialYear)}`;
}

function getPreviewAnnualReportLabel(financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR) {
  return `Annual Report FY ${normalizePreviewFinancialYear(financialYear)}`;
}

const specPageShellStyle = {
  width: "780px",
  background: "#f3f6fb",
  padding: "20px 0 28px",
};

const specPaperStyle = {
  width: "700px",
  margin: "0 auto",
  background: "#FFFFFF",
  boxShadow: "0 14px 34px rgba(15, 23, 42, 0.12)",
  borderRadius: "6px",
  padding: "32px 38px 28px",
  fontFamily: "Helvetica, Arial, sans-serif",
  fontSize: "11px",
  lineHeight: 1.32,
  color: "#000000",
};

const specDataTable = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "11px",
  color: "#000000",
};

const previewPageFrameStyle = {
  width: "780px",
  minHeight: "1080px",
  display: "flex",
  flexDirection: "column",
};

function getPreviewEntityRows(financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR) {
  return [
    ["1.", "Corporate Identity Number (CIN) of the Listed Entity", "L27109KA1964PLC001546"],
    ["2.", "Name of the Listed Entity", "Kennametal India Limited"],
    ["3.", "Year of incorporation", "September 21, 1964"],
    ["4.", "Registered office address", "8/9th Mile, Tumkur Road, Bengaluru, Karnataka - 560073, India"],
    ["5.", "Corporate address", "8/9th Mile, Tumkur Road, Bengaluru, Karnataka - 560073, India"],
    ["6.", "E-mail", "k-bngs-investor.relation@kennametal.com"],
    ["7.", "Telephone", "080-28394321"],
    ["8.", "Website", "https://www.kennametal.com/in/en/about-us/kil-financials.html"],
    ["9.", "Financial year for which reporting is being done", getPreviewFinancialYearLabel(financialYear)],
    ["10.", "Name of the Stock Exchange(s) where shares are listed", "BSE Limited (Scrip code: 505890, Scrip Name: KENNAMET)"],
    ["11.", "Paid-up Capital", "Rs. 219,782,400 /-"],
    ["12.", "Name and contact details (telephone, email address) of the person who may be contacted in case of any queries on the BRSR report", "DIN: 07901688\nMr. Vijaykrishnan Venkatesan\nManaging Director\n+91 8028394321\nvijaykrishnan.venkatesan@kennametal.com"],
    ["13.", "Reporting boundary - Are the disclosures under this report made on a standalone basis (i.e. only for the entity) or on a consolidated basis (i.e. for the entity and all the entities which form a part of its consolidated financial statements, taken together).", "Disclosures made in this report are on a standalone basis and pertain to Kennametal India Limited (KIL). There are no subsidiaries."],
    ["14.", "Name of assurance provider", "Not Applicable"],
    ["15.", "Type of assurance obtained", "Not Applicable"],
  ];
}

const PREVIEW_ACTIVITY_ROWS = [
  ["1.", "Manufacturing and Trading", "Manufacture of other fabricated metal products; metalworking service activities", "87"],
  ["2.", "Manufacturing", "Manufacture of special-purpose machinery", "13"],
];

const PREVIEW_PRODUCT_ROWS = [
  ["1.", "Hard Metal Products", "25910", "87"],
  ["2.", "Machining Solutions Group", "282", "13"],
];

const PREVIEW_OPERATION_ROWS = [
  ["National", "1", "5", "6"],
  ["International*", "Not Applicable", "Not Applicable", "Not Applicable"],
];

const PREVIEW_POLICY_MATRIX_ROWS = [
  ["Whether your entity's policies cover each principle and its core elements.", "YES", "YES", "YES", "YES", "YES"],
  ["Has the policy been approved by the Board?", "YES", "YES", "YES", "YES", "YES"],
  ["Whether the entity has translated the policy into procedures.", "YES", "YES", "YES", "YES", "YES"],
];

const PREVIEW_TRAINING_ROWS = [
  ["Board of Directors", "4", "All Principles", "100%"],
  ["Key Managerial Personnel", "6", "P1, P2, P3 & P6", "100%"],
  ["Employees other than BoD and KMPs", "18", "P1, P3, P5, P6 & P8", "92%"],
];

const PREVIEW_FINES_ROWS = [
  ["Penalty / Fine", "P1", "Regulatory Authority", "Nil", "No material instances reported", "No"],
];

// ─── 1. Spec-Compliant ────────────────────────────────────────────────────────
function SpecCompliantPageOne({ compact = false, logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  const entityRows = compact
    ? [
        ["1.", "Corporate Identity Number (CIN) of the Listed Entity", "L27109KA1964PLC001546"],
        ["2.", "Name of the Listed Entity", "Kennametal India Limited"],
        ["3.", "Year of incorporation", "September 21, 1964"],
        ["4.", "Registered office address", "8/9th Mile, Tumkur Road, Bengaluru"],
        ["5.", "Corporate address", "8/9th Mile, Tumkur Road, Bengaluru"],
      ]
    : getPreviewEntityRows(financialYear);

  return (
    <div style={specPageShellStyle}>
      <div style={specPaperStyle}>
        {/* Header: company name + logo (real or placeholder) */}
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: compact ? "18px" : "28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div style={{ fontSize: compact ? "15px" : "16px", color: "#5b6472" }}>Kennametal India Limited</div>
            {logoSrc ? (
              <img src={logoSrc} alt="Company Logo" style={{ height: compact ? "22px" : "28px", width: "auto", objectFit: "contain" }} />
            ) : (
              <div style={{ position: "relative", width: "34px", height: "24px" }}>
                <div style={{ position: "absolute", right: "10px", top: "0", width: "16px", height: "24px", borderTop: "2px solid #6b7280", borderRight: "2px solid #6b7280", borderBottom: "2px solid #6b7280", transform: "skewX(-2deg)" }} />
                <div style={{ position: "absolute", right: "0", top: "0", width: 0, height: 0, borderTop: "12px solid transparent", borderBottom: "12px solid transparent", borderLeft: "14px solid #10803c" }} />
              </div>
            )}
          </div>
        </div>

        <div style={{ textAlign: "center", marginBottom: compact ? "20px" : "24px" }}>
          <div style={{ fontSize: compact ? "16px" : "18px", fontWeight: "bold" }}>
            Business Responsibility and Sustainability Reporting
          </div>
        </div>

        <div style={{ backgroundColor: "#f1f1f1", marginBottom: "12px", fontSize: compact ? "11px" : "12px", fontWeight: "bold", padding: "4px 10px" }}>
          Section A: General disclosures
        </div>
        <div style={{ display: "flex", marginBottom: "8px", fontWeight: "bold" }}>
          <div style={{ width: "3%" }}>I.</div>
          <div style={{ width: "97%" }}>Details of the listed entity</div>
        </div>
        <div style={{ marginLeft: "18px", marginBottom: compact ? "14px" : "18px" }}>
          {entityRows.map(([num, label, value]) => (
            <div key={num} style={{ display: "flex", margin: compact ? "4px 0" : "6px 0", alignItems: "flex-start" }}>
              <div style={{ width: "4%" }}>{num}</div>
              <div style={{ width: compact ? "43%" : "45%", fontWeight: "bold" }}>{label}</div>
              <div style={{ width: "3%", textAlign: "center" }}>:</div>
              <div style={{ width: compact ? "50%" : "48%", whiteSpace: "pre-line" }}>{value}</div>
            </div>
          ))}
        </div>

        <div style={{ borderTop: "1px solid #000", margin: compact ? "12px 0 14px" : "14px 0 16px" }} />
        <div style={{ display: "flex", marginBottom: "8px", fontWeight: "bold" }}>
          <div style={{ width: "3%" }}>II.</div>
          <div style={{ width: "97%" }}>Products/Services</div>
        </div>
        <div style={{ display: "flex", marginBottom: "8px", fontWeight: "bold" }}>
          <div style={{ width: "3%" }}>16.</div>
          <div style={{ width: "97%" }}>Details of business activities (accounting for 90% of the turnover):</div>
        </div>
        <div style={{ marginLeft: "18px", marginBottom: "12px" }}>
          <table style={specDataTable}>
            <thead>
              <tr>
                {["Sl. No.", "Description of Main Activity", "Description of Business Activity", "% of Turnover of the entity"].map((header, index) => (
                  <th key={header} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: index === 3 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: "left", fontWeight: "bold", color: "#000000" }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PREVIEW_ACTIVITY_ROWS.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${rowIndex}-${cellIndex}`} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: cellIndex === 3 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: cellIndex === 0 || cellIndex === 3 ? "center" : "left", verticalAlign: "top", color: "#000000" }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!compact && (
          <>
            <div style={{ display: "flex", marginBottom: "8px", fontWeight: "bold" }}>
              <div style={{ width: "3%" }}>17.</div>
              <div style={{ width: "97%" }}>Products/Services sold by the entity (accounting for 90% of the entity&apos;s Turnover):</div>
            </div>
            <div style={{ marginLeft: "18px", marginBottom: "12px" }}>
              <table style={specDataTable}>
                <thead>
                  <tr>
                    {["Sl. No.", "Product/Service", "NIC Code", "% of total Turnover contributed"].map((header, index) => (
                      <th key={header} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: index === 3 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: index > 1 ? "center" : "left", fontWeight: "bold", color: "#000000" }}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PREVIEW_PRODUCT_ROWS.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={`${rowIndex}-${cellIndex}`} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: cellIndex === 3 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: cellIndex === 0 || cellIndex >= 2 ? "center" : "left", color: "#000000" }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", marginBottom: "8px", fontWeight: "bold" }}>
              <div style={{ width: "3%" }}>III.</div>
              <div style={{ width: "97%" }}>Operations</div>
            </div>
            <div style={{ display: "flex", marginBottom: "8px", fontWeight: "bold" }}>
              <div style={{ width: "3%" }}>18.</div>
              <div style={{ width: "97%" }}>Number of locations where plants and/or operations/offices of the entity are situated:</div>
            </div>
            <div style={{ marginLeft: "18px" }}>
              <table style={specDataTable}>
                <thead>
                  <tr>
                    {["Location", "Number of plants", "Number of offices", "Total"].map((header, index) => (
                      <th key={header} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: index === 3 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: index === 0 ? "left" : "center", fontWeight: "bold", color: "#000000" }}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PREVIEW_OPERATION_ROWS.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={`${rowIndex}-${cellIndex}`} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: cellIndex === 3 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: cellIndex === 0 ? "left" : "center", fontWeight: cellIndex === 0 ? "bold" : "normal", color: "#000000" }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: "10px", marginTop: "10px" }}>*KIL has operations within India only.</div>
            </div>
          </>
        )}

        <div style={{ marginTop: compact ? "18px" : "52px" }}>
          <div style={{ backgroundColor: "#ffc600", height: "8px", width: "100%", marginBottom: "18px" }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: compact ? "center" : "space-between", fontSize: "12px", fontWeight: compact ? "normal" : "bold" }}>
            {!compact && <span>{getPreviewAnnualReportLabel(financialYear)}</span>}
            <span>1</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpecCompliantPageTwo({ financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  return (
    <div style={specPageShellStyle}>
      <div style={specPaperStyle}>
        <div style={{ backgroundColor: "#f1f1f1", marginBottom: "12px", fontSize: "12px", fontWeight: "bold", padding: "4px 10px" }}>Section B: Management and process disclosures</div>
        <div style={{ marginBottom: "10px", lineHeight: 1.4 }}>This section is aimed at helping businesses demonstrate the structures, policies and processes put in place towards adopting the NGRBC Principles and Core Elements.</div>
        <div style={{ fontWeight: "bold", marginBottom: "10px" }}>Policy and Management Processes</div>
        <table style={{ ...specDataTable, marginBottom: "18px" }}>
          <thead>
            <tr>
              {["Disclosure Questions", "P1", "P2", "P3", "P4", "P5"].map((header, index) => (
                <th key={header} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: index === 5 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: index === 0 ? "left" : "center", fontWeight: "bold", color: "#000000" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_POLICY_MATRIX_ROWS.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={`${rowIndex}-${cellIndex}`} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: cellIndex === 5 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: cellIndex === 0 ? "left" : "center", verticalAlign: "top", color: "#000000" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ backgroundColor: "#f1f1f1", marginBottom: "12px", fontSize: "12px", fontWeight: "bold", padding: "4px 10px" }}>Section C: Principle-wise Performance</div>
        <div style={{ fontWeight: "bold", marginBottom: "10px" }}>Principle 1: Businesses should conduct and govern themselves with integrity, and in a manner that is Ethical, Transparent and Accountable.</div>
        <div style={{ fontWeight: "bold", marginBottom: "10px" }}>Essential Indicators</div>
        <div style={{ display: "flex", marginBottom: "8px", fontWeight: "bold" }}>
          <div style={{ width: "3%" }}>1.</div>
          <div style={{ width: "97%" }}>Percentage coverage by training and awareness programmes on any of the principles during the financial year:</div>
        </div>
        <table style={{ ...specDataTable, marginBottom: "18px" }}>
          <thead>
            <tr>
              {["Segment", "Total number of training and awareness programs held", "Topics / principles covered under the training and its impact", "% of persons in respective category covered by the awareness program"].map((header, index) => (
                <th key={header} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: index === 3 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: index === 0 ? "left" : "center", fontWeight: "bold", color: "#000000" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_TRAINING_ROWS.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={`${rowIndex}-${cellIndex}`} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: cellIndex === 3 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: cellIndex === 0 || cellIndex === 2 ? "left" : "center", color: "#000000" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", marginBottom: "8px", fontWeight: "bold" }}>
          <div style={{ width: "3%" }}>2.</div>
          <div style={{ width: "97%" }}>Details of fines / penalties / punishment / award / compounding fees / settlement amount paid in proceedings with regulators or judicial institutions.</div>
        </div>
        <table style={specDataTable}>
          <thead>
            <tr>
              {["Category", "NGRBC Principle", "Agency / Institution", "Amount (INR)", "Brief of the case", "Appeal preferred?"].map((header, index) => (
                <th key={header} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: index === 5 ? "none" : "1px solid #000", padding: "5px 6px", textAlign: "left", fontWeight: "bold", color: "#000000" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_FINES_ROWS.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={`${rowIndex}-${cellIndex}`} style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000", borderRight: cellIndex === 5 ? "none" : "1px solid #000", padding: "5px 6px", color: "#000000" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: "56px" }}>
          <div style={{ backgroundColor: "#ffc600", height: "8px", width: "100%", marginBottom: "18px" }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "12px", fontWeight: "bold" }}>
            <span>{getPreviewAnnualReportLabel(financialYear)}</span>
            <span>2</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpecCompliantFullPreview({ logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  return (
    <div style={{ width: "780px", background: "#eef2f7", padding: "20px 0", display: "flex", flexDirection: "column", gap: "20px" }}>
      <SpecCompliantPageOne logoSrc={logoSrc} financialYear={financialYear} />
      
      <SpecCompliantPageTwo financialYear={financialYear} />
    </div>
  );
}

// ─── 2. Modern Corporate ──────────────────────────────────────────────────────
function ModernCorporatePreviewPageOne({ logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  const entityRows = getPreviewEntityRows(financialYear);

  return (
    <div style={{ width: "780px", background: "#f3f6fb", padding: "20px 0 28px" }}><div style={{ ...previewPageFrameStyle, background: "#ffffff", boxShadow: "0 6px 22px rgba(15,42,69,0.08)" }}>
      <div style={{ background: "linear-gradient(90deg, #0F2A45 0%, #1A3D5C 38%, #1A6B72 78%, #1F6F7C 100%)", color: "#fff", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", right: "-12px", top: "-20px", width: "150px", height: "150px", borderRadius: "50%", background: "rgba(33,158,245,0.14)" }} />
        <div style={{ position: "absolute", right: "58px", top: "70px", width: "86px", height: "86px", borderRadius: "50%", background: "rgba(33,158,245,0.15)" }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 210px", minHeight: "150px" }}>
          <div style={{ padding: "28px 46px 24px" }}>
            <div style={{ fontFamily: "Georgia, serif", fontSize: "28px", fontWeight: 700, lineHeight: 1.12, marginBottom: "12px" }}>Kennametal India Limited</div>
            <div style={{ width: "78px", height: "3px", background: "#C9922A", marginBottom: "14px" }} />
            <div style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: 700, color: "#E6EDF5" }}>Business Responsibility and Sustainability Report</div>
          </div>
          {/* top-right: logo (white/inverted) + FY badge */}
          <div style={{ padding: "20px 20px 0 0", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
            {logoSrc && (
              <img src={logoSrc} alt="Logo" style={{ height: "32px", width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", opacity: 0.9 }} />
            )}
            <div style={{ background: "#C9922A", color: "#FFFFFF", fontSize: "9px", fontWeight: 700, letterSpacing: "0.16em", padding: "7px 12px", borderRadius: "4px" }}>{getPreviewFinancialYearLabel(financialYear)}</div>
          </div>
        </div>
      </div>
      <div style={{ height: "4px", background: "#C9922A" }} />
      <div style={{ padding: "22px 32px 28px", background: "#ffffff", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#0F2A45", color: "#FFFFFF", padding: "9px 16px", display: "flex", alignItems: "center", gap: "12px", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: 700, marginBottom: "16px" }}>
          <span style={{ width: "3px", height: "18px", background: "#C9922A", display: "inline-block" }} />
          Section A: General Disclosures
        </div>
        <div style={{ background: "#EAF4F5", borderLeft: "3px solid #1A6B72", color: "#0F2A45", padding: "8px 12px", fontSize: "11px", fontWeight: 700, marginBottom: "10px" }}>I. Details of the listed entity</div>
        {entityRows.map(([num, label, value]) => (
          <div key={num} style={{ display: "grid", gridTemplateColumns: "34px 1.6fr 16px 1.55fr", alignItems: "start", gap: "0 10px", padding: "7px 0", borderBottom: num === "15" ? "none" : "1px solid #CDD5E0", fontSize: "10px", lineHeight: 1.32, color: "#1A1A2E" }}>
            <div style={{ color: "#1A6B72", fontWeight: 700, textAlign: "right", paddingRight: "4px" }}>{num}</div>
            <div style={{ fontWeight: 700, color: "#0F2A45" }}>{label}</div>
            <div style={{ color: "#5A6275", textAlign: "center" }}>:</div>
            <div style={{ whiteSpace: "pre-line" }}>{value}</div>
          </div>
        ))}
        <div style={{ marginTop: "24px", background: "#EAF4F5", borderLeft: "3px solid #1A6B72", color: "#0F2A45", padding: "8px 12px", fontSize: "11px", fontWeight: 700, marginBottom: "10px" }}>II. Products/services</div>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#0F2A45", marginBottom: "8px" }}>16. Details of business activities (accounting for 90% of the turnover)</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", marginBottom: "10px" }}>
          <thead>
            <tr style={{ background: "#0F2A45", borderBottom: "2px solid #C9922A" }}>
              {["Sl. No.", "Description of Main Activity", "Description of Business Activity", "% of Turnover of the entity"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: index === 0 || index === 3 ? "center" : "left", padding: "7px 10px", fontWeight: 700, borderRight: index === 3 ? "none" : "1px solid rgba(255,255,255,0.2)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_ACTIVITY_ROWS.map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? "#F7FAFC" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-activity-${cellIndex}`} style={{ padding: "8px 10px", borderBottom: "1px solid #CDD5E0", borderRight: cellIndex === 3 ? "none" : "1px solid #CDD5E0", textAlign: cellIndex === 0 || cellIndex === 3 ? "center" : "left", color: "#1A1A2E" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#0F2A45", marginBottom: "8px" }}>17. Products/Services sold by the entity (accounting for 90% of the entity's Turnover)</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", marginBottom: "10px" }}>
          <thead>
            <tr style={{ background: "#0F2A45", borderBottom: "2px solid #C9922A" }}>
              {["Sl. No.", "Product/Service", "NIC Code", "% of total Turnover contributed"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: index === 0 || index >= 2 ? "center" : "left", padding: "7px 10px", fontWeight: 700, borderRight: index === 3 ? "none" : "1px solid rgba(255,255,255,0.2)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_PRODUCT_ROWS.map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? "#F7FAFC" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-product-${cellIndex}`} style={{ padding: "8px 10px", borderBottom: "1px solid #CDD5E0", borderRight: cellIndex === 3 ? "none" : "1px solid #CDD5E0", textAlign: cellIndex === 0 || cellIndex >= 2 ? "center" : "left", color: "#1A1A2E" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ background: "#EAF4F5", borderLeft: "3px solid #1A6B72", color: "#0F2A45", padding: "8px 12px", fontSize: "11px", fontWeight: 700, marginBottom: "10px", marginTop: "12px" }}>III. Operations</div>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#0F2A45", lineHeight: 1.4, marginBottom: "8px" }}>18. Number of locations where plants and/or operations/offices of the entity are situated:</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", marginBottom: "12px" }}>
          <thead>
            <tr style={{ background: "#0F2A45", borderBottom: "2px solid #C9922A" }}>
              {["Location", "Number of plants", "Number of offices", "Total"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: index === 0 ? "left" : "center", padding: "7px 10px", fontWeight: 700, borderRight: index === 3 ? "none" : "1px solid rgba(255,255,255,0.2)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_OPERATION_ROWS.map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? "#F7FAFC" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-ops-${cellIndex}`} style={{ padding: "7px 10px", borderBottom: "1px solid #CDD5E0", borderRight: cellIndex === 3 ? "none" : "1px solid #CDD5E0", textAlign: cellIndex === 0 ? "left" : "center", color: "#1A1A2E", fontWeight: cellIndex === 0 ? 700 : 400 }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: "9px", fontStyle: "italic", color: "#5A6275", background: "#FDFAF4", borderLeft: "2px solid #C9922A", padding: "5px 10px", marginBottom: "16px" }}>*KIL has operations within India only.</div>
        <div style={{ marginTop: "auto", background: "#0F2A45", color: "rgba(255,255,255,.75)", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "9px" }}>
          <span>{getPreviewAnnualReportLabel(financialYear)} · Kennametal India Limited</span>
          <span style={{ color: "#C9922A", fontWeight: 700 }}>1</span>
        </div>
      </div>
    </div></div>
  );
}

function ModernCorporatePreviewPageTwo({ financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  return (
    <div style={{ width: "780px", background: "#f3f6fb", padding: "20px 0 28px" }}><div style={{ ...previewPageFrameStyle, background: "#ffffff", boxShadow: "0 6px 22px rgba(15,42,69,0.08)" }}>
      <div style={{ padding: "28px 32px 34px", background: "#ffffff", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#0F2A45", color: "#FFFFFF", padding: "9px 16px", display: "flex", alignItems: "center", gap: "12px", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: 700, marginBottom: "16px" }}>
          <span style={{ width: "3px", height: "18px", background: "#C9922A", display: "inline-block" }} />
          Section B: Management and Process Disclosures
        </div>
        <div style={{ marginBottom: "10px", lineHeight: 1.4, color: "#5A6275", fontSize: "10px" }}>This section is aimed at helping businesses demonstrate the structures, policies and processes put in place towards adopting the NGRBC Principles and Core Elements.</div>
        <div style={{ background: "#EAF4F5", borderLeft: "3px solid #1A6B72", color: "#0F2A45", padding: "8px 14px", fontSize: "11px", fontWeight: 700, marginBottom: "10px" }}>Policy and Management Processes</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginBottom: "18px" }}>
          <thead>
            <tr style={{ background: "#0F2A45", borderBottom: "2px solid #C9922A" }}>
              {["Disclosure Questions", "P1", "P2", "P3", "P4", "P5"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: index === 0 ? "left" : "center", padding: "7px 8px", fontWeight: 700, borderRight: index === 5 ? "none" : "1px solid rgba(255,255,255,0.2)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_POLICY_MATRIX_ROWS.map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? "#F7FAFC" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-${cellIndex}`} style={{ padding: "8px 8px", borderBottom: "1px solid #CDD5E0", borderRight: cellIndex === 5 ? "none" : "1px solid #CDD5E0", textAlign: cellIndex === 0 ? "left" : "center", color: cellIndex === 0 ? "#0F2A45" : "#1A1A2E", fontWeight: cellIndex === 0 ? 700 : 600, verticalAlign: "top" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ background: "#0F2A45", color: "#FFFFFF", padding: "9px 16px", display: "flex", alignItems: "center", gap: "12px", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: 700, marginBottom: "16px" }}>
          <span style={{ width: "3px", height: "18px", background: "#C9922A", display: "inline-block" }} />
          Section C: Principle-wise Performance
        </div>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#0F2A45", marginBottom: "8px" }}>Principle 1: Businesses should conduct and govern themselves with integrity, and in a manner that is Ethical, Transparent and Accountable.</div>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#0F2A45", marginBottom: "8px" }}>Essential Indicators</div>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#0F2A45", marginBottom: "8px" }}>1. Percentage coverage by training and awareness programmes on any of the principles during the financial year:</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginBottom: "16px" }}>
          <thead>
            <tr style={{ background: "#0F2A45", borderBottom: "2px solid #C9922A" }}>
              {["Segment", "Total number of training and awareness programs held", "Topics / principles covered under the training and its impact", "% of persons in respective category covered by the awareness program"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: index === 0 ? "left" : "center", padding: "7px 8px", fontWeight: 700, borderRight: index === 3 ? "none" : "1px solid rgba(255,255,255,0.2)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_TRAINING_ROWS.map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? "#F7FAFC" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-training-${cellIndex}`} style={{ padding: "8px 8px", borderBottom: "1px solid #CDD5E0", borderRight: cellIndex === 3 ? "none" : "1px solid #CDD5E0", textAlign: cellIndex === 0 || cellIndex === 2 ? "left" : "center", color: "#1A1A2E" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#0F2A45", marginBottom: "8px" }}>2. Details of fines / penalties / punishment / award / compounding fees / settlement amount paid in proceedings with regulators or judicial institutions.</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginBottom: "18px" }}>
          <thead>
            <tr style={{ background: "#0F2A45", borderBottom: "2px solid #C9922A" }}>
              {["Category", "NGRBC Principle", "Agency / Institution", "Amount (INR)", "Brief of the case", "Appeal preferred?"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: "left", padding: "7px 8px", fontWeight: 700, borderRight: index === 5 ? "none" : "1px solid rgba(255,255,255,0.2)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_FINES_ROWS.map((row, idx) => (
              <tr key={idx} style={{ background: "#F7FAFC" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-fines-${cellIndex}`} style={{ padding: "8px 8px", borderBottom: "1px solid #CDD5E0", borderRight: cellIndex === 5 ? "none" : "1px solid #CDD5E0", color: "#1A1A2E" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: "auto", background: "#0F2A45", color: "rgba(255,255,255,.75)", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "9px" }}>
          <span>{getPreviewAnnualReportLabel(financialYear)} · Kennametal India Limited</span>
          <span style={{ color: "#C9922A", fontWeight: 700 }}>2</span>
        </div>
      </div>
    </div>
</div>
  );
}

function ModernCorporateFullPreview({ logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  return (
    <div style={{ width: "780px", background: "#eef2f7", padding: "20px 0", display: "flex", flexDirection: "column", gap: "18px" }}>
      <ModernCorporatePreviewPageOne logoSrc={logoSrc} financialYear={financialYear} />
      <ModernCorporatePreviewPageTwo financialYear={financialYear} />
    </div>
  );
}

// ─── 3. Dark Prestige ─────────────────────────────────────────────────────────
function DarkPrestigePreviewPageOne({ logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  const entityRows = getPreviewEntityRows(financialYear);
  const productRows = PREVIEW_PRODUCT_ROWS;

  return (
    <div style={{ width: "780px", background: "#f3f6fb", padding: "20px 0 28px" }}><div style={{ ...previewPageFrameStyle, background: "#ffffff", boxShadow: "0 6px 22px rgba(15,42,69,0.08)" }}>
      {/* Dark header */}
      <div style={{ background: "#1A1A1A", padding: "26px 34px 16px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, right: 0, width: "190px", height: "150px", background: "#C8972A", clipPath: "polygon(100% 0, 0 0, 100% 100%)", opacity: 0.14 }} />
        {/* Logo absolutely positioned top-right */}
        {logoSrc && (
          <div style={{ position: "absolute", top: "16px", right: "16px", zIndex: 2 }}>
            <img src={logoSrc} alt="Logo" style={{ height: "36px", width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", opacity: 0.85 }} />
          </div>
        )}
        <div style={{ width: "44px", height: "2px", background: "#C8972A", marginBottom: "12px" }} />
        <div style={{ fontFamily: "Courier New, monospace", fontSize: "7px", letterSpacing: ".16em", textTransform: "uppercase", color: "#C8972A", marginBottom: "8px" }}>Annual Report · ESG Disclosure</div>
        <div style={{ fontFamily: "Georgia, serif", fontSize: "20px", fontWeight: 700, color: "#FFFFFF", lineHeight: 1.1, marginBottom: "12px" }}>Business Responsibility &amp;<br />Sustainability Reporting</div>
        <div style={{ display: "flex", gap: "12px" }}>
          {[["Entity", "Kennametal India Limited"], ["Financial Year", getPreviewFinancialYearLabel(financialYear)]].map(([label, value]) => (
            <div key={label} style={{ border: "1px solid rgba(255,255,255,0.12)", padding: "5px 8px", minWidth: label === "Entity" ? "150px" : "100px" }}>
              <div style={{ fontFamily: "Courier New, monospace", fontSize: "6px", letterSpacing: ".12em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: "2px" }}>{label}</div>
              <div style={{ fontSize: "8px", fontWeight: 600, color: "rgba(255,255,255,0.86)" }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ height: "4px", background: "linear-gradient(90deg, #C8972A, #E7C678, #C8972A)" }} />
      <div style={{ padding: "18px 22px 18px", background: "#ffffff", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#1A1A1A", color: "#FFFFFF", padding: "7px 14px", display: "flex", alignItems: "center", gap: "8px", fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: 700, marginBottom: "10px" }}>
          <span style={{ width: "2px", height: "16px", background: "#C8972A", display: "inline-block" }} />
          Section A: General Disclosures
        </div>
        <div style={{ background: "#FBF8F2", borderLeft: "3px solid #C8972A", color: "#2A2A2A", padding: "6px 10px", fontSize: "10px", fontWeight: 700, marginBottom: "6px" }}>I. Details of the listed entity</div>
        {entityRows.map(([num, label, value]) => (
          <div key={num} style={{ display: "grid", gridTemplateColumns: "30px 1.45fr 14px 1.45fr", alignItems: "start", gap: "0 8px", padding: "5px 0", fontSize: "9px", lineHeight: 1.28, color: "#2A2A2A" }}>
            <div style={{ color: "#C8972A", fontWeight: 700, textAlign: "right", paddingRight: "2px" }}>{num}</div>
            <div style={{ fontWeight: 700, color: "#343434" }}>{label}</div>
            <div style={{ color: "#9B9385", textAlign: "center" }}>:</div>
            <div style={{ whiteSpace: "pre-line" }}>{value}</div>
          </div>
        ))}
        <div style={{ marginTop: "18px", background: "#FBF8F2", borderLeft: "3px solid #C8972A", color: "#2A2A2A", padding: "6px 10px", fontSize: "10px", fontWeight: 700, marginBottom: "8px" }}>II. Products / Services</div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "8px" }}>
          <span style={{ fontFamily: "Courier New, monospace", fontSize: "9px", color: "#C8972A", fontWeight: 700 }}>16</span>
          <span style={{ fontSize: "9.5px", fontWeight: 700, color: "#2A2A2A", lineHeight: 1.35 }}>Products / Services sold by the entity (accounting for 90% of the entity&apos;s Turnover)</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginBottom: "18px" }}>
          <thead>
            <tr style={{ background: "#1A1A1A", borderBottom: "2px solid #C8972A" }}>
              {["Sl. No.", "Product / Service", "NIC Code", "% of total Turnover"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: index >= 2 ? "center" : "left", padding: "7px 8px", fontWeight: 700, borderRight: index === 3 ? "none" : "1px solid rgba(255,255,255,0.12)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {productRows.map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? "#FCFAF6" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-${cellIndex}`} style={{ padding: "7px 8px", borderBottom: "1px solid #E6DDCF", borderRight: cellIndex === 3 ? "none" : "1px solid #E6DDCF", textAlign: cellIndex === 0 || cellIndex >= 2 ? "center" : "left", color: "#2A2A2A" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ background: "#FBF8F2", borderLeft: "3px solid #C8972A", color: "#2A2A2A", padding: "6px 10px", fontSize: "10px", fontWeight: 700, marginBottom: "8px" }}>III. Operations</div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "8px" }}>
          <span style={{ fontFamily: "Courier New, monospace", fontSize: "9px", color: "#C8972A", fontWeight: 700 }}>18</span>
          <span style={{ fontSize: "9.5px", fontWeight: 700, color: "#2A2A2A", lineHeight: 1.35 }}>Number of locations where plants and/or operations/offices of the entity are situated</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginBottom: "10px" }}>
          <thead>
            <tr style={{ background: "#1A1A1A", borderBottom: "2px solid #C8972A" }}>
              {["Location", "Number of Plants", "Number of Offices", "Total"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: index === 0 ? "left" : "center", padding: "7px 8px", fontWeight: 700, borderRight: index === 3 ? "none" : "1px solid rgba(255,255,255,0.12)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[["National", "1", "5", "6"], ["International*", "Not Applicable", "Not Applicable", "Not Applicable"]].map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? "#FCFAF6" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-ops-${cellIndex}`} style={{ padding: "7px 8px", borderBottom: "1px solid #E6DDCF", borderRight: cellIndex === 3 ? "none" : "1px solid #E6DDCF", textAlign: cellIndex === 0 ? "left" : "center", color: "#2A2A2A" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", gap: "8px", padding: "8px 10px", background: "#FBF6EC", borderLeft: "3px solid #C8972A", fontSize: "8.5px", color: "#7E766A", fontStyle: "italic", marginBottom: "16px" }}>
          <span style={{ color: "#C8972A" }}>✦</span><span>KIL has operations within India only.</span>
        </div>
        <div style={{ marginTop: "auto", background: "#1A1A1A", color: "rgba(255,255,255,0.95)", padding: "7px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "8px", fontWeight: 700 }}>
          <span>{getPreviewAnnualReportLabel(financialYear)} · Kennametal India Limited</span>
          <span>BRSR · Standalone Disclosure</span>
        </div>
      </div>
    </div>
  </div>
  );
}

function DarkPrestigePreviewPageTwo({ financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  return (
    <div style={{ width: "780px", background: "#f3f6fb", padding: "20px 0 28px" }}><div style={{ ...previewPageFrameStyle, background: "#ffffff", boxShadow: "0 6px 22px rgba(15,42,69,0.08)" }}>
      <div style={{ padding: "18px 22px 18px", background: "#ffffff", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#1A1A1A", color: "#FFFFFF", padding: "7px 14px", display: "flex", alignItems: "center", gap: "8px", fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: 700, marginBottom: "12px" }}>
          <span style={{ width: "2px", height: "16px", background: "#C8972A", display: "inline-block" }} />
          Section B: Management and Process Disclosures
        </div>
        <div style={{ marginBottom: "10px", lineHeight: 1.4, color: "#7E766A", fontSize: "9px" }}>This section is aimed at helping businesses demonstrate the structures, policies and processes put in place towards adopting the NGRBC Principles and Core Elements.</div>
        <div style={{ background: "#FBF8F2", borderLeft: "3px solid #C8972A", color: "#2A2A2A", padding: "6px 10px", fontSize: "10px", fontWeight: 700, marginBottom: "10px" }}>Policy and Management Processes</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginBottom: "18px" }}>
          <thead>
            <tr style={{ background: "#1A1A1A", borderBottom: "2px solid #C8972A" }}>
              {["Disclosure Questions", "P1", "P2", "P3", "P4", "P5"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: index === 0 ? "left" : "center", padding: "7px 6px", fontWeight: 700, borderRight: index === 5 ? "none" : "1px solid rgba(255,255,255,0.12)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_POLICY_MATRIX_ROWS.map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? "#FCFAF6" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-matrix-${cellIndex}`} style={{ padding: "7px 6px", borderBottom: "1px solid #E6DDCF", borderRight: cellIndex === 5 ? "none" : "1px solid #E6DDCF", textAlign: cellIndex === 0 ? "left" : "center", color: cellIndex === 0 ? "#2A2A2A" : "#343434", fontWeight: cellIndex === 0 ? 700 : 600, verticalAlign: "top" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ background: "#1A1A1A", color: "#FFFFFF", padding: "7px 14px", display: "flex", alignItems: "center", gap: "8px", fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: 700, marginBottom: "10px" }}>
          <span style={{ width: "2px", height: "16px", background: "#C8972A", display: "inline-block" }} />
          Section C: Principle-wise Performance
        </div>
        <div style={{ fontSize: "9.5px", fontWeight: 700, color: "#2A2A2A", marginBottom: "8px" }}>Principle 1: Businesses should conduct and govern themselves with integrity, and in a manner that is Ethical, Transparent and Accountable.</div>
        <div style={{ background: "#FBF8F2", borderLeft: "3px solid #C8972A", color: "#2A2A2A", padding: "6px 10px", fontSize: "10px", fontWeight: 700, marginBottom: "8px" }}>Essential Indicators</div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "8px" }}>
          <span style={{ fontFamily: "Courier New, monospace", fontSize: "9px", color: "#C8972A", fontWeight: 700 }}>1</span>
          <span style={{ fontSize: "9.5px", fontWeight: 700, color: "#2A2A2A", lineHeight: 1.35 }}>Percentage coverage by training and awareness programmes on any of the principles during the financial year</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginBottom: "16px" }}>
          <thead>
            <tr style={{ background: "#1A1A1A", borderBottom: "2px solid #C8972A" }}>
              {["Segment", "Total number of training and awareness programs held", "Topics / principles covered under the training and its impact", "% of persons in respective category covered by the awareness program"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: index === 0 ? "left" : "center", padding: "7px 8px", fontWeight: 700, borderRight: index === 3 ? "none" : "1px solid rgba(255,255,255,0.12)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_TRAINING_ROWS.map((row, idx) => (
              <tr key={idx} style={{ background: idx % 2 === 0 ? "#FCFAF6" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-train-${cellIndex}`} style={{ padding: "7px 8px", borderBottom: "1px solid #E6DDCF", borderRight: cellIndex === 3 ? "none" : "1px solid #E6DDCF", textAlign: cellIndex === 0 ? "left" : "center", color: "#2A2A2A" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "8px" }}>
          <span style={{ fontFamily: "Courier New, monospace", fontSize: "9px", color: "#C8972A", fontWeight: 700 }}>2</span>
          <span style={{ fontSize: "9.5px", fontWeight: 700, color: "#2A2A2A", lineHeight: 1.35 }}>Details of fines / penalties / punishment / award / compounding fees / settlement amount paid in proceedings with regulators or judicial institutions.</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginBottom: "10px" }}>
          <thead>
            <tr style={{ background: "#1A1A1A", borderBottom: "2px solid #C8972A" }}>
              {["Category", "NGRBC Principle", "Agency / Institution", "Amount (INR)", "Brief of the case", "Appeal preferred?"].map((header, index) => (
                <th key={header} style={{ color: "#FFFFFF", textAlign: "left", padding: "7px 8px", fontWeight: 700, borderRight: index === 5 ? "none" : "1px solid rgba(255,255,255,0.12)" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_FINES_ROWS.map((row, idx) => (
              <tr key={idx} style={{ background: "#FCFAF6" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-fines-${cellIndex}`} style={{ padding: "7px 8px", borderBottom: "1px solid #E6DDCF", borderRight: cellIndex === 5 ? "none" : "1px solid #E6DDCF", textAlign: "left", color: "#2A2A2A" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: "auto", background: "#1A1A1A", color: "rgba(255,255,255,0.95)", padding: "7px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "8px", fontWeight: 700 }}>
          <span>{getPreviewAnnualReportLabel(financialYear)} · Kennametal India Limited</span>
          <span>BRSR · Standalone Disclosure</span>
        </div>
      </div>
    </div></div>
  );
}

function DarkPrestigeFullPreview({ logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  return (
    <div style={{ width: "780px", background: "#eef2f7", padding: "20px 0", display: "flex", flexDirection: "column", gap: "20px" }}>
      <DarkPrestigePreviewPageOne logoSrc={logoSrc} financialYear={financialYear} />
      
      <DarkPrestigePreviewPageTwo financialYear={financialYear} />
    </div>
  );
}

// ─── 4. Clean Light ───────────────────────────────────────────────────────────
function CleanLightPreviewPageOne({ logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  const entityRows = getPreviewEntityRows(financialYear);

  return (
    <div style={{ width: "780px", background: "#f3f6fb", padding: "20px 0 28px" }}><div style={{ ...previewPageFrameStyle, background: "#ffffff", boxShadow: "0 6px 22px rgba(27,58,92,0.08)" }}>
      <div style={{ background: "#ffffff", borderBottom: "1px solid #E2E8F0", overflow: "hidden", position: "relative" }}>
        <div style={{ height: "6px", background: "#1B3A5C" }} />
        <div style={{ padding: "36px 60px 30px", position: "relative" }}>
          {/* Logo replaces decorative bars in top-right */}
          <div style={{ position: "absolute", right: "60px", top: "36px" }}>
            {logoSrc ? (
              <img src={logoSrc} alt="Logo" style={{ height: "44px", width: "auto", objectFit: "contain", opacity: 0.85 }} />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                {[80, 52, 32].map((w) => (
                  <div key={w} style={{ width: `${w}px`, height: "3px", background: "#E8EFF7", borderRadius: "2px" }} />
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "18px" }}>
            <div style={{ width: "28px", height: "1px", background: "#D4A820" }} />
            <span style={{ fontFamily: "monospace", fontSize: "8px", letterSpacing: ".22em", textTransform: "uppercase", color: "#B8860B" }}>Annual ESG Disclosure</span>
          </div>
          <div style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: 700, color: "#1B3A5C", lineHeight: 1.12, marginBottom: "6px" }}>
            Business Responsibility &amp;<br />Sustainability Reporting
          </div>
          <div style={{ fontSize: "11px", color: "#4A5568", marginBottom: "22px" }}>Kennametal India Limited · {getPreviewFinancialYearLabel(financialYear)}</div>
          <div style={{ display: "flex", border: "1px solid #E2E8F0", borderRadius: "5px", overflow: "hidden", width: "fit-content" }}>
            {[["Entity", "Kennametal India Limited"], ["Financial Year", getPreviewFinancialYearLabel(financialYear)]].map(([label, value], idx) => (
              <div key={label} style={{ padding: "10px 18px", borderRight: idx === 0 ? "1px solid #E2E8F0" : "none" }}>
                <div style={{ fontFamily: "monospace", fontSize: "7px", letterSpacing: ".16em", textTransform: "uppercase", color: "#718096" }}>{label}</div>
                <div style={{ fontSize: "10px", fontWeight: 600, color: "#1B3A5C" }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ height: "3px", background: "#D4A820" }} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 60px", background: "#F2F6FB", borderTop: "1px solid #E2E8F0", borderBottom: "1px solid #E2E8F0", position: "relative" }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", background: "#1B3A5C" }} />
        <span style={{ fontFamily: "monospace", fontSize: "8px", letterSpacing: ".2em", textTransform: "uppercase", color: "#4A7AB5", background: "#E8EFF7", padding: "2px 8px", borderRadius: "3px" }}>Section A</span>
        <span style={{ fontSize: "10px", fontWeight: 600, color: "#1B3A5C" }}>General Disclosures</span>
      </div>

      <div style={{ padding: "16px 60px 28px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "14px", paddingBottom: "10px", marginBottom: "14px", borderBottom: "1px solid #EDEFF3" }}>
          <span style={{ fontFamily: "monospace", fontSize: "10px", color: "#4A7AB5" }}>I.</span>
          <span style={{ fontSize: "11px", fontWeight: 800, color: "#1B3A5C", textTransform: "uppercase", letterSpacing: ".07em" }}>Details of the Listed Entity</span>
        </div>
        {entityRows.map(([n, label, value]) => (
          <div key={n} style={{ display: "grid", gridTemplateColumns: "26px 1.1fr 14px 1.45fr", gap: "0 10px", alignItems: "baseline", padding: "7px 0", borderBottom: "1px solid #EDF2F7", fontSize: "10px" }}>
            <span style={{ fontFamily: "monospace", fontSize: "8.5px", color: "#4A7AB5", fontWeight: 600 }}>{n}</span>
            <span style={{ fontWeight: 700, color: "#2D3748" }}>{label}</span>
            <span style={{ color: "#CBD5E0", textAlign: "center" }}>:</span>
            <span style={{ color: "#1A1A2E" }}>{value}</span>
          </div>
        ))}
        <div style={{ marginTop: "18px", display: "flex", alignItems: "baseline", gap: "14px", paddingBottom: "10px", marginBottom: "12px", borderBottom: "1px solid #EDEFF3" }}>
          <span style={{ fontFamily: "monospace", fontSize: "10px", color: "#4A7AB5" }}>II.</span>
          <span style={{ fontSize: "11px", fontWeight: 800, color: "#1B3A5C", textTransform: "uppercase", letterSpacing: ".07em" }}>Products / Services</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "10px" }}>
          <span style={{ fontFamily: "monospace", fontSize: "8.5px", color: "#4A7AB5", fontWeight: 700 }}>16</span>
          <span style={{ fontSize: "10.5px", fontWeight: 700, color: "#2D3748", lineHeight: 1.45 }}>Details of business activities (accounting for 90% of the turnover)</span>
        </div>
        <div style={{ marginBottom: "14px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
            <thead>
              <tr>
                {["Sl. No.", "Description of Main Activity", "Description of Business Activity", "% of Turnover"].map((header, index) => (
                  <th key={header} style={{ padding: "9px 12px 8px", textAlign: index === 3 ? "center" : "left", fontSize: "8px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#718096", borderBottom: "2px solid #1B3A5C" }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PREVIEW_ACTIVITY_ROWS.map((row, rowIndex) => (
                <tr key={rowIndex} style={{ borderBottom: "1px solid #EEF2F7" }}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${rowIndex}-${cellIndex}`} style={{ padding: "9px 12px", textAlign: cellIndex === 0 || cellIndex === 3 ? "center" : "left", color: "#1A1A2E", lineHeight: 1.4 }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "10px" }}>
          <span style={{ fontFamily: "monospace", fontSize: "8.5px", color: "#4A7AB5", fontWeight: 700 }}>17</span>
          <span style={{ fontSize: "10.5px", fontWeight: 700, color: "#2D3748", lineHeight: 1.45 }}>Products / Services sold by the entity (accounting for 90% of the entity&apos;s Turnover)</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", marginBottom: "10px" }}>
          <thead>
            <tr>
              {["Sl. No.", "Product / Service", "NIC Code", "% of total Turnover"].map((header, index) => (
                <th key={header} style={{ padding: "9px 12px 8px", textAlign: index >= 2 ? "center" : "left", fontSize: "8px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#718096", borderBottom: "2px solid #1B3A5C" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_PRODUCT_ROWS.map((row, rowIndex) => (
              <tr key={rowIndex} style={{ borderBottom: "1px solid #EEF2F7" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${rowIndex}-p-${cellIndex}`} style={{ padding: "9px 12px", textAlign: cellIndex === 0 || cellIndex >= 2 ? "center" : "left", color: "#1A1A2E" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: "18px", display: "flex", alignItems: "baseline", gap: "14px", paddingBottom: "10px", marginBottom: "12px", borderBottom: "1px solid #EDEFF3" }}>
          <span style={{ fontFamily: "monospace", fontSize: "10px", color: "#4A7AB5" }}>III.</span>
          <span style={{ fontSize: "11px", fontWeight: 800, color: "#1B3A5C", textTransform: "uppercase", letterSpacing: ".07em" }}>Operations</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "10px" }}>
          <span style={{ fontFamily: "monospace", fontSize: "8.5px", color: "#4A7AB5", fontWeight: 700 }}>18</span>
          <span style={{ fontSize: "10.5px", fontWeight: 700, color: "#2D3748" }}>Number of locations where plants and/or operations/offices are situated:</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", marginBottom: "12px" }}>
          <thead>
            <tr>
              {["Location", "Number of plants", "Number of offices", "Total"].map((header, index) => (
                <th key={header} style={{ padding: "9px 12px 8px", textAlign: index === 0 ? "left" : "center", fontSize: "8px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#718096", borderBottom: "2px solid #1B3A5C" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_OPERATION_ROWS.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #EEF2F7" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-ops-${cellIndex}`} style={{ padding: "9px 12px", textAlign: cellIndex === 0 ? "left" : "center", color: "#1A1A2E", fontWeight: cellIndex === 0 ? 700 : 400 }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", gap: "8px", padding: "10px 14px", background: "#F7FAFC", borderLeft: "3px solid #4A7AB5", borderRadius: "0 6px 6px 0", fontSize: "9.5px", color: "#71717A", marginBottom: "18px" }}>
          <span style={{ color: "#4A7AB5", fontSize: "9px" }}>▸</span>
          <span>KIL has operations within India only.</span>
        </div>
        <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "18px" }}>
          <span style={{ fontSize: "10px", fontWeight: 700, color: "#718096" }}>{getPreviewAnnualReportLabel(financialYear)} · Kennametal India Limited</span>
          <span style={{ fontFamily: "monospace", fontSize: "8.5px", color: "#1B3A5C" }}>BRSR · Standalone Disclosure</span>
        </div>
      </div>
    </div></div>
  );
}

function CleanLightPreviewPageTwo({ financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  return (
    <div style={{ width: "780px", background: "#f3f6fb", padding: "20px 0 28px" }}><div style={{ ...previewPageFrameStyle, background: "#ffffff", boxShadow: "0 6px 22px rgba(27,58,92,0.08)" }}>
      <div style={{ padding: "22px 60px 30px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", background: "#F2F6FB", borderTop: "1px solid #E2E8F0", borderBottom: "1px solid #E2E8F0", position: "relative", marginBottom: "14px" }}>
          <div style={{ position: "absolute", left: "-60px", top: 0, bottom: 0, width: "4px", background: "#1B3A5C" }} />
          <span style={{ fontFamily: "monospace", fontSize: "8px", letterSpacing: ".2em", textTransform: "uppercase", color: "#4A7AB5", background: "#E8EFF7", padding: "2px 8px", borderRadius: "3px" }}>Section B</span>
          <span style={{ fontSize: "10px", fontWeight: 600, color: "#1B3A5C" }}>Management and Process Disclosures</span>
        </div>
        <div style={{ fontSize: "9.5px", lineHeight: 1.5, color: "#718096", marginBottom: "12px" }}>
          This section is aimed at helping businesses demonstrate the structures, policies and processes put in place towards adopting the NGRBC Principles and Core Elements.
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: "14px", paddingBottom: "10px", marginBottom: "12px", borderBottom: "1px solid #EDEFF3" }}>
          <span style={{ fontFamily: "monospace", fontSize: "10px", color: "#4A7AB5" }}>—</span>
          <span style={{ fontSize: "11px", fontWeight: 800, color: "#1B3A5C", textTransform: "uppercase", letterSpacing: ".07em" }}>Policy and Management Processes</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "8.5px", marginBottom: "16px" }}>
          <thead>
            <tr>
              {["Disclosure Questions", "P1", "P2", "P3", "P4", "P5"].map((header, index) => (
                <th key={header} style={{ padding: "8px 8px 7px", textAlign: index === 0 ? "left" : "center", fontSize: "7px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#718096", borderBottom: "2px solid #1B3A5C" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_POLICY_MATRIX_ROWS.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #EEF2F7", background: idx % 2 === 0 ? "#F8FBFD" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-matrix-${cellIndex}`} style={{ padding: "8px 8px", textAlign: cellIndex === 0 ? "left" : "center", color: cellIndex === 0 ? "#2D3748" : "#1A1A2E", fontWeight: cellIndex === 0 ? 700 : 500, verticalAlign: "top" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", background: "#F2F6FB", borderTop: "1px solid #E2E8F0", borderBottom: "1px solid #E2E8F0", position: "relative", marginBottom: "14px" }}>
          <div style={{ position: "absolute", left: "-60px", top: 0, bottom: 0, width: "4px", background: "#1B3A5C" }} />
          <span style={{ fontFamily: "monospace", fontSize: "8px", letterSpacing: ".2em", textTransform: "uppercase", color: "#4A7AB5", background: "#E8EFF7", padding: "2px 8px", borderRadius: "3px" }}>Section C</span>
          <span style={{ fontSize: "10px", fontWeight: 600, color: "#1B3A5C" }}>Principle-wise Performance</span>
        </div>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#2D3748", marginBottom: "8px", lineHeight: 1.45 }}>Principle 1: Businesses should conduct and govern themselves with integrity, and in a manner that is Ethical, Transparent and Accountable.</div>
        <div style={{ background: "#F7FAFC", borderLeft: "3px solid #4A7AB5", padding: "8px 12px", marginBottom: "8px", fontSize: "10px", fontWeight: 700, color: "#2D3748" }}>Essential Indicators</div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "8px" }}>
          <span style={{ fontFamily: "monospace", fontSize: "8.5px", color: "#4A7AB5", fontWeight: 700 }}>1</span>
          <span style={{ fontSize: "10px", fontWeight: 700, color: "#2D3748", lineHeight: 1.45 }}>Percentage coverage by training and awareness programmes on any of the principles during the financial year:</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginBottom: "16px" }}>
          <thead>
            <tr>
              {["Segment", "Total number of training and awareness programs held", "Topics / principles covered under the training and its impact", "% of persons in respective category covered by the awareness program"].map((header, index) => (
                <th key={header} style={{ padding: "8px 10px 7px", textAlign: index === 0 ? "left" : "center", fontSize: "7px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#718096", borderBottom: "2px solid #1B3A5C" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_TRAINING_ROWS.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #EEF2F7", background: idx % 2 === 0 ? "#F8FBFD" : "#FFFFFF" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-training-${cellIndex}`} style={{ padding: "8px 10px", textAlign: cellIndex === 0 || cellIndex === 2 ? "left" : "center", color: "#1A1A2E" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "8px" }}>
          <span style={{ fontFamily: "monospace", fontSize: "8.5px", color: "#4A7AB5", fontWeight: 700 }}>2</span>
          <span style={{ fontSize: "10px", fontWeight: 700, color: "#2D3748", lineHeight: 1.45 }}>Details of fines / penalties / punishment / award / compounding fees / settlement amount paid in proceedings with regulators or judicial institutions.</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginBottom: "18px" }}>
          <thead>
            <tr>
              {["Category", "NGRBC Principle", "Agency / Institution", "Amount (INR)", "Brief of the case", "Appeal preferred?"].map((header) => (
                <th key={header} style={{ padding: "8px 10px 7px", textAlign: "left", fontSize: "7px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#718096", borderBottom: "2px solid #1B3A5C" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_FINES_ROWS.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #EEF2F7", background: "#F8FBFD" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-fine-${cellIndex}`} style={{ padding: "8px 10px", textAlign: "left", color: "#1A1A2E" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 0" }}>
          <span style={{ fontSize: "10px", fontWeight: 700, color: "#718096" }}>{getPreviewAnnualReportLabel(financialYear)} · Kennametal India Limited</span>
          <span style={{ fontFamily: "monospace", fontSize: "8.5px", color: "#1B3A5C" }}>BRSR · Standalone Disclosure</span>
        </div>
      </div>
    </div>
</div>
  );
}

function CleanLightFullPreview({ logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  return (
    <div style={{ width: "780px", background: "#eef2f7", padding: "20px 0", display: "flex", flexDirection: "column", gap: "20px" }}>
      <CleanLightPreviewPageOne logoSrc={logoSrc} financialYear={financialYear} />
      
      <CleanLightPreviewPageTwo financialYear={financialYear} />
    </div>
  );
}

// ─── 5. Editorial Teal ────────────────────────────────────────────────────────
function EditorialTealCover({ logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  const chips = [["Financial Year", getPreviewFinancialYearLabel(financialYear)], ["Entity", "Kennametal India Limited"]];
  return (
    <section style={{ background: "#F5F0E8", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: "6px", background: "#0D6E6E" }} />
      {logoSrc && (
        <div style={{ position: "absolute", top: "28px", right: "44px" }}>
          <img src={logoSrc} alt="Logo" style={{ height: "32px", width: "auto", objectFit: "contain" }} />
        </div>
      )}
      <div style={{ padding: "40px 44px 34px 50px" }}>
        <h1 style={{ fontFamily: "Lora, Georgia, serif", fontSize: "28px", lineHeight: 1.08, margin: "0 0 22px", color: "#18181B", fontWeight: 700 }}>
          Business Responsibility<br />
          <em style={{ color: "#0D6E6E", fontStyle: "italic", fontWeight: 700 }}>&amp; Sustainability Reporting</em>
        </h1>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {chips.map(([label, value]) => (
            <div key={label} style={{ padding: "8px 14px", background: "#FFFFFF", border: "1px solid #D4C9B8", borderRadius: "100px" }}>
              <div style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "7px", letterSpacing: ".15em", textTransform: "uppercase", color: "#A1A1AA", marginBottom: "2px" }}>{label}</div>
              <div style={{ fontSize: "10px", fontWeight: 700, color: "#18181B" }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EditorialTealSectionBand({ label, title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "10px 44px", background: "#0D6E6E" }}>
      <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "8px", letterSpacing: ".2em", textTransform: "uppercase", color: "rgba(255,255,255,.55)", border: "1px solid rgba(255,255,255,.2)", borderRadius: "100px", padding: "2px 10px" }}>{label}</span>
      <span style={{ fontSize: "10px", fontWeight: 700, color: "#FFFFFF", letterSpacing: ".04em" }}>{title}</span>
    </div>
  );
}

function EditorialTealPreviewPageOne({ logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  const entityRows = getPreviewEntityRows(financialYear);

  return (
    <div style={{ width: "780px", background: "#F5F0E8", padding: "20px 0 28px" }}><div style={{ ...previewPageFrameStyle, background: "#FFFFFF", color: "#18181B", fontFamily: "Syne, Helvetica, Arial, sans-serif", boxShadow: "0 6px 22px rgba(24,24,27,0.08)" }}>
      <EditorialTealCover logoSrc={logoSrc} financialYear={financialYear} />
      <EditorialTealSectionBand label="Section A" title="General Disclosures" />
      <div style={{ padding: "22px 44px 0", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "14px", paddingBottom: "10px", marginBottom: "14px", borderBottom: "1px solid #EDE5D8" }}>
          <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "10px", color: "#0D6E6E" }}>I.</span>
          <span style={{ fontSize: "11px", fontWeight: 800, color: "#18181B", textTransform: "uppercase", letterSpacing: ".07em" }}>Details of the Listed Entity</span>
        </div>
        {entityRows.map(([n, label, value]) => (
          <div key={label} style={{ display: "grid", gridTemplateColumns: "24px 1fr 12px 1.5fr", alignItems: "baseline", gap: "0 10px", padding: "7px 0", borderBottom: "1px solid #F4F4F5", fontSize: "10.5px" }}>
            <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "8.5px", color: "#0D6E6E" }}>{n}</span>
            <span style={{ fontWeight: 700, color: "#3F3F46" }}>{label}</span>
            <span style={{ color: "#A1A1AA", textAlign: "center" }}>:</span>
            <span style={{ color: "#18181B" }}>{value}</span>
          </div>
        ))}
        <div style={{ marginTop: "18px", display: "flex", alignItems: "baseline", gap: "14px", paddingBottom: "10px", marginBottom: "14px", borderBottom: "1px solid #EDE5D8" }}>
          <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "10px", color: "#0D6E6E" }}>II.</span>
          <span style={{ fontSize: "11px", fontWeight: 800, color: "#18181B", textTransform: "uppercase", letterSpacing: ".07em" }}>Products / Services</span>
        </div>
        <div style={{ marginBottom: "10px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: "22px", height: "22px", borderRadius: "5px", background: "#E0F2F2", color: "#0A5555", fontFamily: "IBM Plex Mono, monospace", fontSize: "8.5px" }}>16</span>
          <span style={{ fontSize: "10.5px", fontWeight: 700, color: "#3F3F46", lineHeight: 1.45 }}>Details of business activities accounting for 90% of the turnover</span>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
            <thead>
              <tr>
                {["Sl. No.", "Description of Main Activity", "Description of Business Activity", "% of Turnover"].map((header, index) => (
                  <th key={header} style={{ padding: "9px 12px 8px", textAlign: index === 3 ? "center" : "left", fontSize: "8px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#71717A", borderBottom: "2px solid #18181B" }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PREVIEW_ACTIVITY_ROWS.map((row, rowIndex) => (
                <tr key={rowIndex} style={{ borderBottom: "1px solid #F0F0F0" }}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} style={{ padding: "9px 12px", textAlign: cellIndex === 0 || cellIndex === 3 ? "center" : "left", color: "#18181B", lineHeight: 1.45 }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginBottom: "10px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: "22px", height: "22px", borderRadius: "5px", background: "#E0F2F2", color: "#0A5555", fontFamily: "IBM Plex Mono, monospace", fontSize: "8.5px" }}>17</span>
          <span style={{ fontSize: "10.5px", fontWeight: 700, color: "#3F3F46", lineHeight: 1.45 }}>Products/Services sold by the entity (accounting for 90% of the entity&apos;s Turnover)</span>
        </div>
        <div style={{ marginBottom: "18px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
            <thead>
              <tr>
                {["Sl. No.", "Product/Service", "NIC Code", "% of total Turnover contributed"].map((header, index) => (
                  <th key={header} style={{ padding: "9px 12px 8px", textAlign: index === 0 || index >= 2 ? "center" : "left", fontSize: "8px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#71717A", borderBottom: "2px solid #18181B" }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PREVIEW_PRODUCT_ROWS.map((row, rowIndex) => (
                <tr key={rowIndex} style={{ borderBottom: "1px solid #F0F0F0" }}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${rowIndex}-product-${cellIndex}`} style={{ padding: "9px 12px", textAlign: cellIndex === 0 || cellIndex >= 2 ? "center" : "left", color: "#18181B", lineHeight: 1.45 }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: "18px", display: "flex", alignItems: "baseline", gap: "14px", paddingBottom: "10px", marginBottom: "14px", borderBottom: "1px solid #EDE5D8" }}>
          <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "10px", color: "#0D6E6E" }}>III.</span>
          <span style={{ fontSize: "11px", fontWeight: 800, color: "#18181B", textTransform: "uppercase", letterSpacing: ".07em" }}>Operations</span>
        </div>
        <div style={{ marginBottom: "12px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: "22px", height: "22px", borderRadius: "5px", background: "#E0F2F2", color: "#0A5555", fontFamily: "IBM Plex Mono, monospace", fontSize: "8.5px" }}>18</span>
          <span style={{ fontSize: "10.5px", fontWeight: 700, color: "#3F3F46", lineHeight: 1.45 }}>Number of locations where plants and/or operations/offices of the entity are situated:</span>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
            <thead>
              <tr>
                {["Location", "Number of plants", "Number of offices", "Total"].map((header, index) => (
                  <th key={header} style={{ padding: "9px 12px 8px", textAlign: index === 0 ? "left" : "center", fontSize: "8px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#71717A", borderBottom: "2px solid #18181B" }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PREVIEW_OPERATION_ROWS.map((row, rowIndex) => (
                <tr key={rowIndex} style={{ borderBottom: "1px solid #F0F0F0" }}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${rowIndex}-ops-${cellIndex}`} style={{ padding: "9px 12px", textAlign: cellIndex === 0 ? "left" : "center", color: "#18181B", fontWeight: cellIndex === 0 ? 700 : 400 }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", gap: "8px", padding: "10px 14px", background: "#F0FAFA", borderLeft: "3px solid #0D6E6E", borderRadius: "0 6px 6px 0", fontSize: "9.5px", color: "#71717A", marginBottom: "20px" }}>
          <span style={{ color: "#0D6E6E", fontSize: "9px" }}>▸</span>
          <span>KIL has operations within India only.</span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 44px", background: "#184E52", marginTop: "auto" }}>
        <span style={{ fontSize: "10px", fontWeight: 700, color: "rgba(255,255,255,.55)" }}>{getPreviewAnnualReportLabel(financialYear)}</span>
        <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "8.5px", color: "#E0F2F2" }}>BRSR · Standalone Disclosure</span>
      </div>
    </div>
</div>
  );
}

function EditorialTealPreviewPageTwo({ financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  return (
    <div style={{ width: "780px", background: "#F5F0E8", padding: "20px 0 28px" }}><div style={{ ...previewPageFrameStyle, background: "#FFFFFF", color: "#18181B", fontFamily: "Syne, Helvetica, Arial, sans-serif", boxShadow: "0 6px 22px rgba(24,24,27,0.08)" }}>
      <div style={{ padding: "22px 44px 0", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ margin: "0 -44px 18px" }}>
          <EditorialTealSectionBand label="Section B" title="Management and Process Disclosures" />
        </div>
        <div style={{ marginBottom: "12px", fontSize: "10px", lineHeight: 1.5, color: "#71717A" }}>
          This section is aimed at helping businesses demonstrate the structures, policies and processes put in place towards adopting the NGRBC Principles and Core Elements.
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: "14px", paddingBottom: "10px", marginBottom: "14px", borderBottom: "1px solid #EDE5D8" }}>
          <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "10px", color: "#0D6E6E" }}>—</span>
          <span style={{ fontSize: "11px", fontWeight: 800, color: "#18181B", textTransform: "uppercase", letterSpacing: ".07em" }}>Policy and Management Processes</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9.5px", marginBottom: "18px" }}>
          <thead>
            <tr>
              {["Disclosure Questions", "P1", "P2", "P3", "P4", "P5"].map((header, index) => (
                <th key={header} style={{ padding: "9px 10px 8px", textAlign: index === 0 ? "left" : "center", fontSize: "8px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#71717A", borderBottom: "2px solid #18181B" }}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PREVIEW_POLICY_MATRIX_ROWS.map((row, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #F0F0F0" }}>
                {row.map((cell, cellIndex) => (
                  <td key={`${idx}-${cellIndex}`} style={{ padding: "9px 10px", textAlign: cellIndex === 0 ? "left" : "center", color: cellIndex === 0 ? "#3F3F46" : "#18181B", fontWeight: cellIndex === 0 ? 700 : 600 }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ margin: "0 -44px 18px" }}>
          <EditorialTealSectionBand label="Section C" title="Principle-wise Performance" />
        </div>
        <div style={{ fontSize: "10.5px", fontWeight: 700, color: "#3F3F46", lineHeight: 1.45, marginBottom: "8px" }}>
          Principle 1: Businesses should conduct and govern themselves with integrity, and in a manner that is Ethical, Transparent and Accountable.
        </div>
        <div style={{ fontSize: "10px", fontWeight: 800, color: "#18181B", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: "10px" }}>Essential Indicators</div>
        <div style={{ marginBottom: "10px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: "22px", height: "22px", borderRadius: "5px", background: "#E0F2F2", color: "#0A5555", fontFamily: "IBM Plex Mono, monospace", fontSize: "8.5px" }}>1</span>
          <span style={{ fontSize: "10.5px", fontWeight: 700, color: "#3F3F46", lineHeight: 1.45 }}>Percentage coverage by training and awareness programmes on any of the principles during the financial year:</span>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
            <thead>
              <tr>
                {["Segment", "Total number of training and awareness programs held", "Topics / principles covered under the training and its impact", "% of persons in respective category covered by the awareness program"].map((header, index) => (
                  <th key={header} style={{ padding: "9px 10px 8px", textAlign: index === 0 || index === 2 ? "left" : "center", fontSize: "8px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#71717A", borderBottom: "2px solid #18181B" }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PREVIEW_TRAINING_ROWS.map((row, rowIndex) => (
                <tr key={rowIndex} style={{ borderBottom: "1px solid #F0F0F0" }}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${rowIndex}-training-${cellIndex}`} style={{ padding: "9px 10px", textAlign: cellIndex === 0 || cellIndex === 2 ? "left" : "center", color: "#18181B" }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginBottom: "10px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: "22px", height: "22px", borderRadius: "5px", background: "#E0F2F2", color: "#0A5555", fontFamily: "IBM Plex Mono, monospace", fontSize: "8.5px" }}>2</span>
          <span style={{ fontSize: "10.5px", fontWeight: 700, color: "#3F3F46", lineHeight: 1.45 }}>Details of fines / penalties / punishment / award / compounding fees / settlement amount paid in proceedings with regulators or judicial institutions.</span>
        </div>
        <div style={{ marginBottom: "16px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
            <thead>
              <tr>
                {["Category", "NGRBC Principle", "Agency / Institution", "Amount (INR)", "Brief of the case", "Appeal preferred?"].map((header) => (
                  <th key={header} style={{ padding: "9px 10px 8px", textAlign: "left", fontSize: "8px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#71717A", borderBottom: "2px solid #18181B" }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PREVIEW_FINES_ROWS.map((row, rowIndex) => (
                <tr key={rowIndex} style={{ borderBottom: "1px solid #F0F0F0" }}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${rowIndex}-fines-${cellIndex}`} style={{ padding: "9px 10px", textAlign: "left", color: "#18181B" }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 44px", background: "#184E52", marginTop: "auto" }}>
        <span style={{ fontSize: "10px", fontWeight: 700, color: "rgba(255,255,255,.55)" }}>{getPreviewAnnualReportLabel(financialYear)} · Kennametal India Limited</span>
        <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: "8.5px", color: "#E0F2F2" }}>BRSR · Standalone Disclosure</span>
      </div>
    </div>
</div>
  );
}

function EditorialTealFullPreview({ logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR }) {
  return (
    <div style={{ width: "780px", background: "#eef2f7", padding: "20px 0", display: "flex", flexDirection: "column", gap: "20px" }}>
      <EditorialTealPreviewPageOne logoSrc={logoSrc} financialYear={financialYear} />
      
      <EditorialTealPreviewPageTwo financialYear={financialYear} />
    </div>
  );
}

// ─── Factory export ───────────────────────────────────────────────────────────
export function getReportTemplatePreviews(logoSrc = null, financialYear = DEFAULT_PREVIEW_FINANCIAL_YEAR) {
  return [
    {
      id: 1,
      name: "Spec-Compliant",
      desc: "Minimal · Audit-ready",
      getPreview:     (logo) => <SpecCompliantPageOne compact logoSrc={logo} financialYear={financialYear} />,
      getFullPreview: (logo) => <SpecCompliantFullPreview logoSrc={logo} financialYear={financialYear} />,
    },
    {
      id: 2,
      name: "Modern Corporate",
      desc: "Navy · Teal · Gold",
      getPreview:     () => <ModernCorporatePreviewPageOne financialYear={financialYear} />,
      getFullPreview: () => <ModernCorporateFullPreview financialYear={financialYear} />,
    },
    {
      id: 3,
      name: "Dark Prestige",
      desc: "Dark cover · Gold rule",
      getPreview:     () => <DarkPrestigePreviewPageOne financialYear={financialYear} />,
      getFullPreview: () => <DarkPrestigeFullPreview financialYear={financialYear} />,
    },
    {
      id: 4,
      name: "Clean Light",
      desc: "Navy slim · Light bg",
      getPreview:     () => <CleanLightPreviewPageOne financialYear={financialYear} />,
      getFullPreview: () => <CleanLightFullPreview financialYear={financialYear} />,
    },
    {
      id: 5,
      name: "Editorial Teal",
      desc: "Lora · Syne · Sand",
      getPreview:     () => <EditorialTealPreviewPageOne financialYear={financialYear} />,
      getFullPreview: () => <EditorialTealFullPreview financialYear={financialYear} />,
    },
  ];
}
